import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _styled from "styled-components";
var _excluded = ["error", "defaultValue", "displayFormat", "disabled", "label", "hintMessage", "suffix", "loading", "icon", "rightTextLink", "bgColor", "alwaysShowAsHint", "onChange", "onPaste", "autoComplete", "enableValidation", "maxDateValidation", "minDateValidation", "errorCustom"];
var _jsxFileName = "/home/bamboo/bamboo-agent-home/xml-data/build-dir/DLS-WEB7-JOB1/src/components/InputText/InputText.js";
import React, { useRef, useState, useEffect } from 'react';
import MaskedInput from './MaskedInput';
import styled, { css } from 'styled-components';
import { noop, testIdProps } from '../../helpers';
import FormField from '../../core/components/Forms/FormField';
import FormInput from '../../core/components/Forms/FormInput';
import LabelText from '../../core/components/Forms/LabelText';
import HintText from '../../core/components/Forms/HintText';
import FormFieldHint from '../../core/components/Forms/FormFieldHint';
import { withTheme, useTheme } from '../../core/ThemeProvider';
import loadingAnimation from '@dls/assets/lottiefiles/loading';
import { responsiveStyles } from '../../core/utils/responsiveStyles';
import { Fade } from '../../core/animation';
import Animation from '../Animation';
import { validateDate, isEndTyping } from './dateValidation';
import supportedFormats from './supportedFormats';
var LoadingOverlay = styled.div.withConfig({
  displayName: "InputText__LoadingOverlay",
  componentId: "sc-1wwpxbl-0"
})(["position:relative;top:0;left:0;width:100%;height:100%;display:flex;align-items:center;justify-content:center;"]);
/* eslint-disable-next-line react/display-name */

var InputText = /*#__PURE__*/React.forwardRef(function InputText(props, ref) {
  var _this = this;

  /*eslint no-unused-vars: ["error", { "ignoreRestSiblings": true }]*/
  var error = props.error,
    defaultValue = props.defaultValue,
    displayFormat = props.displayFormat,
    disabled = props.disabled,
    label = props.label,
    hintMessage = props.hintMessage,
    suffix = props.suffix,
    loading = props.loading,
    icon = props.icon,
    rightTextLink = props.rightTextLink,
    bgColor = props.bgColor,
    alwaysShowAsHint = props.alwaysShowAsHint,
    onChange = props.onChange,
    onPaste = props.onPaste,
    autoComplete = props.autoComplete,
    enableValidation = props.enableValidation,
    maxDateValidation = props.maxDateValidation,
    minDateValidation = props.minDateValidation,
    errorCustom = props.errorCustom,
    rest = _objectWithoutProperties(props, _excluded);
  var _useTheme = useTheme(),
    theme = _useTheme.theme;
  var value = useRef(defaultValue);
  var _useState = useState({
      hintMessage: hintMessage,
      error: error
    }),
    _useState2 = _slicedToArray(_useState, 2),
    errorInfo = _useState2[0],
    setErrorInfo = _useState2[1];
  useEffect(function () {
    setErrorInfo({
      hintMessage: hintMessage,
      error: error
    });
  }, [hintMessage, error]);
  var handleRightTextLinkClick = function handleRightTextLinkClick(e) {
    blockFieldInteraction(e);
    rightTextLink.onClick && rightTextLink.onClick(e, value.current);
  };
  var _onChange = function _onChange(e, rawValue) {
    value.current = e.target.value;
    var invalidDate = displayFormat && enableValidation && isEndTyping(e.target.value, supportedFormats[displayFormat]);
    if (invalidDate) {
      var errorMessage = validateDate({
        formattedDate: value.current,
        minDateValidation: minDateValidation || 0,
        maxDateValidation: maxDateValidation,
        errorCustom: errorCustom
      });
      setErrorInfo({
        hintMessage: errorMessage,
        error: !!errorMessage
      });
      return;
    }
    if (error) {
      setErrorInfo({
        hintMessage: hintMessage,
        error: error
      });
    } else {
      setErrorInfo({
        hintMessage: '',
        error: false
      });
    }
    if (typeof onChange === 'function') {
      onChange(e, rawValue);
    }
  };
  var renderInputSuffix = function renderInputSuffix() {
    if (suffix) return suffix;
    /* keep it for now, as it might be used in other projects */

    if (icon) return /*#__PURE__*/React.createElement(AdornmentImage, {
      src: icon.src,
      alt: icon.alt,
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 116,
        columnNumber: 22
      }
    });
    /* keep it for now, as it might be used in other projects */

    if (rightTextLink) return /*#__PURE__*/React.createElement(AdornmentText, {
      onClick: handleRightTextLinkClick,
      "data-testid": "textLinkId",
      __self: _this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 121,
        columnNumber: 9
      }
    }, rightTextLink.text);
    return null;
  };
  return /*#__PURE__*/React.createElement("div", _extends({}, testIdProps(props), {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 133,
      columnNumber: 5
    }
  }), /*#__PURE__*/React.createElement(FormField, {
    disabled: disabled,
    defaultValue: defaultValue,
    error: errorInfo.error,
    bgColor: bgColor,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 134,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(InputContainer, {
    loading: loading,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 140,
      columnNumber: 9
    }
  }, displayFormat ? /*#__PURE__*/React.createElement(MaskedInput, _extends({}, rest, testIdProps({
    'data-testid': props.inputDataTestId
  }, 'inputField'), {
    onChange: _onChange,
    ref: ref,
    disabled: disabled,
    displayFormat: displayFormat,
    defaultValue: defaultValue,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 142,
      columnNumber: 13
    }
  })) : /*#__PURE__*/React.createElement(FormInput, _extends({}, rest, testIdProps({
    'data-testid': props.inputDataTestId
  }, 'inputField'), {
    autoComplete: autoComplete,
    onChange: _onChange,
    onPaste: onPaste,
    disabled: disabled,
    defaultValue: defaultValue,
    ref: ref,
    alwaysShowAsHint: alwaysShowAsHint,
    as: "input",
    error: props.error,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 155,
      columnNumber: 13
    }
  })), (suffix || icon || rightTextLink) && /*#__PURE__*/React.createElement(InputAdornment, {
    onClick: blockFieldInteraction,
    onMouseDown: blockFieldInteraction,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 174,
      columnNumber: 13
    }
  }, renderInputSuffix()), /*#__PURE__*/React.createElement(LabelText, {
    "data-testid": "input-label",
    alwaysShowAsHint: alwaysShowAsHint,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 182,
      columnNumber: 11
    }
  }, label)), /*#__PURE__*/React.createElement(_StyledFade, {
    visible: loading,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 189,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(LoadingOverlay, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 199,
      columnNumber: 11
    }
  }, /*#__PURE__*/React.createElement(Animation, {
    "data-testid": "loading-animation",
    source: loadingAnimation,
    size: "22px",
    color: theme.cl_sec_d1,
    action: "play",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 200,
      columnNumber: 13
    }
  })))), errorInfo.hintMessage && /*#__PURE__*/React.createElement(FormFieldHint, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 211,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(HintText, {
    error: errorInfo.error,
    "data-testid": "hintId",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 212,
      columnNumber: 11
    }
  }, errorInfo.hintMessage)));
});
InputText.defaultProps = {
  disabled: false,
  error: false,
  onChange: noop,
  type: 'text',
  bgColor: 'white'
};
// Prevent click and mouse events to propagate down to the input field.

var blockFieldInteraction = function blockFieldInteraction(e) {
  e.preventDefault();
  e.stopPropagation();
};
/**
 * Container for placing UI on the left or the right side of the input field.
 * @param {'end'|'begin'} position Where to place the UI.
 */

var InputAdornment = styled.div.withConfig({
  displayName: "InputText__InputAdornment",
  componentId: "sc-1wwpxbl-1"
})(function (p) {
  return css(["position:relative;bottom:10px;display:flex;padding:0 0 0 8px;flex-direction:column;justify-content:center;align-items:center;", ""], p.position === 'end' ? {
    left: 0
  } : {
    right: 0
  });
});
var AdornmentImage = styled.img.withConfig({
  displayName: "InputText__AdornmentImage",
  componentId: "sc-1wwpxbl-2"
})(["width:40px;"]);
var AdornmentText = withTheme(styled.span.withConfig({
  displayName: "InputText__AdornmentText",
  componentId: "sc-1wwpxbl-3"
})(["padding:4px 0;font-family:", ";color:", ";direction:rtl;", " ", " ", ""], function (p) {
  var _p$coreTheme$inputTex, _p$coreTheme, _p$coreTheme$inputTex2;
  return (_p$coreTheme$inputTex = (_p$coreTheme = p.coreTheme) === null || _p$coreTheme === void 0 ? void 0 : (_p$coreTheme$inputTex2 = _p$coreTheme.inputText) === null || _p$coreTheme$inputTex2 === void 0 ? void 0 : _p$coreTheme$inputTex2.fontFamily) !== null && _p$coreTheme$inputTex !== void 0 ? _p$coreTheme$inputTex : p.coreTheme.ff_sec_bold;
}, function (p) {
  return p.coreTheme.cl_sec_d1;
}, function (p) {
  return p.onClick && {
    cursor: 'pointer'
  };
}, function (_ref) {
  var _coreTheme$inputText, _responsiveStyles, _coreTheme$inputText2;
  var coreTheme = _ref.coreTheme;
  return (coreTheme === null || coreTheme === void 0 ? void 0 : (_coreTheme$inputText = coreTheme.inputText) === null || _coreTheme$inputText === void 0 ? void 0 : _coreTheme$inputText.fontSize) && {
    fontSize: (_responsiveStyles = responsiveStyles({
      fontSize: (_coreTheme$inputText2 = coreTheme.inputText) === null || _coreTheme$inputText2 === void 0 ? void 0 : _coreTheme$inputText2.fontSize
    }, coreTheme)) === null || _responsiveStyles === void 0 ? void 0 : _responsiveStyles.fontSize
  };
}, function (_ref2) {
  var _coreTheme$inputText3, _coreTheme$inputText4;
  var coreTheme = _ref2.coreTheme;
  return (coreTheme === null || coreTheme === void 0 ? void 0 : (_coreTheme$inputText3 = coreTheme.inputText) === null || _coreTheme$inputText3 === void 0 ? void 0 : _coreTheme$inputText3.fontWeightAdornmentText) && {
    fontWeight: coreTheme === null || coreTheme === void 0 ? void 0 : (_coreTheme$inputText4 = coreTheme.inputText) === null || _coreTheme$inputText4 === void 0 ? void 0 : _coreTheme$inputText4.fontWeightAdornmentText
  };
}));
var InputContainer = styled.div.withConfig({
  displayName: "InputText__InputContainer",
  componentId: "sc-1wwpxbl-4"
})(["display:flex;height:30px;", ""], function (p) {
  return p.loading && "\n opacity: 0.5\n  ";
});
export default InputText;
var _StyledFade = styled(Fade).withConfig({
  displayName: "InputText___StyledFade",
  componentId: "sc-1wwpxbl-5"
})({
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0
});